import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import {
  StudioBooking,
  ExternalBooking,
  BookingDateType,
  DATE_TYPE_LABELS
} from '../../services/studio-bookings';
import { EXTERNAL_CHIP_CLASS } from './BookingModal';

const SESSION_ORDER: Record<BookingDateType, number> = {
  morning: 0,
  full_day: 1,
  multi_day: 1,
  afternoon: 2
};

interface DayBookingsPopoverProps {
  label: string;
  bookings: StudioBooking[];
  externalBookings: ExternalBooking[];
  chipColors: Record<string, string>;
  roomColorMap: Record<string, string>;
  defaultRoomColor: string;
  alignRight: boolean;
  anchorBottom: boolean;
  canOpen: boolean;
  onSelectBooking: (b: StudioBooking) => void;
  onClose: () => void;
}

export function DayBookingsPopover({
  label, bookings, externalBookings, chipColors, roomColorMap, defaultRoomColor,
  alignRight, anchorBottom, canOpen, onSelectBooking, onClose
}: DayBookingsPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const sorted = [...bookings].sort(
    (a, b) => SESSION_ORDER[a.dateType] - SESSION_ORDER[b.dateType] || a.roomName.localeCompare(b.roomName)
  );

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Bookings on ${label}`}
      className={`absolute z-30 w-72 max-h-80 overflow-y-auto bg-white border border-[#e2e8f0] rounded-xl shadow-lg p-2 ${
        alignRight ? 'right-1' : 'left-1'
      } ${anchorBottom ? 'bottom-1' : 'top-8'}`}
    >
      <div className="flex items-center justify-between mb-1.5 px-0.5">
        <p className="text-[11px] font-semibold text-navy">{label}</p>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-slate-400 hover:text-slate-600 p-0.5 rounded hover:bg-slate-50"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="space-y-1">
        {sorted.map(b => (
          <button
            key={b.bookingId}
            onClick={() => { if (canOpen) onSelectBooking(b); }}
            className={`w-full text-left px-2 py-1 rounded border-l-4 ${chipColors[b.status]} ${canOpen ? '' : 'cursor-default'}`}
            style={{ borderLeftColor: roomColorMap[b.roomId] || defaultRoomColor }}
          >
            <span className="block text-[11px] font-semibold truncate">{b.customer || b.facilitatorName}</span>
            <span className="block text-[10px] opacity-80 truncate">
              {b.roomName} · {DATE_TYPE_LABELS[b.dateType]}
            </span>
          </button>
        ))}
      </div>

      {externalBookings.length > 0 && (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-2 mb-1 px-0.5">
            External (not synced)
          </p>
          <div className="space-y-1">
            {externalBookings.map((e, i) => (
              <div key={`${e.roomId}-${i}`} className={`px-2 py-1 rounded ${EXTERNAL_CHIP_CLASS}`}>
                <span className="block text-[11px] font-medium truncate">{e.roomName}</span>
                <span className="block text-[10px] truncate">
                  {e.startDateTime.slice(11, 16)}–{e.endDateTime.slice(11, 16)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
